import { z } from 'zod';
import { dateOnlySchema, uuidSchema } from './common.js';
/**
 * `ownerId` scopes to that owner's current properties (via `ownerships`,
 * same table an ownership row already gates access through — step 4).
 * `tenantId` scopes to leases that tenant is named on right now (via
 * `lease_tenants`) — a removed roommate does not match. Neither is a
 * substitute for RLS: an owner or tenant hitting these endpoints already
 * sees only their own rows regardless of these params, which exist so
 * *staff* can generate the same one-owner or one-tenant view instead of
 * reading it out of the unfiltered portfolio by eye.
 *
 * Omitted from reports with no lease/tenant concept in their output
 * (`vacancy`, `depositsHeld` are grouped by property, not by lease — a
 * vacant unit has no current tenant, and deposits held has no per-lease row
 * to filter down to).
 */
const ownerScope = { ownerId: uuidSchema.optional() };
const tenantScope = { tenantId: uuidSchema.optional() };
/** AR aging — the delinquency view, bucketed. `propertyId` filters like every other report. */
export const arAgingQuerySchema = z.object({
    propertyId: uuidSchema.optional(),
    ...ownerScope,
    ...tenantScope,
    minDaysLate: z.coerce.number().int().min(0).max(3650).default(0),
});
export const vacancyQuerySchema = z.object({
    propertyId: uuidSchema.optional(),
    ...ownerScope,
});
export const leaseExpirationsQuerySchema = z.object({
    propertyId: uuidSchema.optional(),
    ...ownerScope,
    ...tenantScope,
    withinDays: z.coerce.number().int().min(1).max(3650).default(120),
});
export const depositsHeldQuerySchema = z.object({
    propertyId: uuidSchema.optional(),
    ...ownerScope,
});
/**
 * Deposits collected by settlement date — a date range, not a "now" view
 * (CLAUDE.md: this one is required, and it's the client's manual-escrow-
 * transfer input, so it has to be runnable for any settlement window, not
 * just today's).
 */
export const depositsCollectedQuerySchema = z.object({
    from: dateOnlySchema,
    to: dateOnlySchema,
    propertyId: uuidSchema.optional(),
    ...ownerScope,
    ...tenantScope,
});
export const complianceClocksQuerySchema = z.object({
    propertyId: uuidSchema.optional(),
    ...ownerScope,
    ...tenantScope,
});
/**
 * The "master report" for one owner or one tenant — every ownership/lease/
 * charge/payment/notice event touching their account, chronological. Unlike
 * every report above, the owner/tenant here isn't a narrowing filter on a
 * portfolio-wide view — it's required, because there's no meaningful
 * portfolio-wide version of "this one account's history."
 */
export const ownerReportQuerySchema = z.object({
    ownerId: uuidSchema,
    from: dateOnlySchema.optional(),
    to: dateOnlySchema.optional(),
});
export const tenantReportQuerySchema = z.object({
    tenantId: uuidSchema,
    from: dateOnlySchema.optional(),
    to: dateOnlySchema.optional(),
});
/**
 * The same "master report" shape for one property — every ownership, lease,
 * charge, payment, fee, owner statement line, work order cost, and notice on
 * it. `propertyId` is required for the same reason `ownerId`/`tenantId` are
 * above.
 */
export const propertyReportQuerySchema = z.object({
    propertyId: uuidSchema,
    from: dateOnlySchema.optional(),
    to: dateOnlySchema.optional(),
});
/**
 * Every payment in the system, both directions — tenant rent payments in,
 * owner disbursements out, fees included on each row — portfolio-wide by
 * default like the reports above (not required-scope like ownerReport/
 * tenantReport above). No `propertyId`: an owner disbursement isn't
 * property-scoped (it's computed per owner across every property they
 * hold), so a property filter would silently behave differently for the
 * two directions instead of narrowing both the same way.
 */
export const paymentsHistoryQuerySchema = z.object({
    ...ownerScope,
    ...tenantScope,
});
/**
 * Work order / ticket activity, grouped by vendor or by property — cost
 * included. Live, not a frozen artifact: `from`/`to` are both optional
 * (unlike `depositsCollectedQuerySchema`'s required pair) and default to
 * all time when omitted, filtered on `work_orders.created_at` — when the
 * ticket was opened, the natural "activity in this period" anchor, since
 * an open ticket has no `completed_at` yet. No `tenantId`: work orders
 * aren't tenant-scoped in a reporting sense. `vendorId` narrows either
 * report to one vendor (meaningful even on the by-property report, same
 * as `propertyId` narrows the vendor-grouped one).
 */
export const maintenanceByVendorQuerySchema = z.object({
    vendorId: uuidSchema.optional(),
    propertyId: uuidSchema.optional(),
    ...ownerScope,
    from: dateOnlySchema.optional(),
    to: dateOnlySchema.optional(),
});
export const maintenanceByPropertyQuerySchema = z.object({
    propertyId: uuidSchema.optional(),
    vendorId: uuidSchema.optional(),
    ...ownerScope,
    from: dateOnlySchema.optional(),
    to: dateOnlySchema.optional(),
});
//# sourceMappingURL=reports.js.map